// MODE.A2 (#1264) — delivery-mode switch card for the project dashboard.
//
// Completes the UI half of MODE.A2: the backend PATCH /projects/:id
// { deliveryMode } shipped API-only in #1171. This card lets an eligible
// (Free-Trial) project switch delivery mode post-creation, wired to that
// existing PATCH and surfacing its structured 403/409 error codes (A4 /
// #1173) inline.
//
// Modeled 1:1 on ProjectDomainPackCard: read-only current value + "Change"
// → inline editor → Apply (PATCH) → inline error slot → onChange refresh.
//
// ── INVERTED GATING POLARITY (vs creation) ────────────────────────────
// At CREATION the DeliveryModePicker is HIDDEN for Trial tenants
// (NewProjectModal `{!isTrialTenant && …}`) — Trial auto-defaults at the
// API layer and doesn't choose. The SWITCH is the opposite: the backend
// allows a mode change ONLY for Trial tenants (403 otherwise), so the
// editable control is SHOWN for Trial and read-only for everyone else.
// Same `billingState === 'TRIAL'` test, INVERTED polarity. Do not copy
// creation's `{!isTrialTenant}` gating here.
//
// Gating is FAIL-CLOSED: an unknown/loading billingState yields NO
// editable control (never an optimistic one that the backend would 403).
//
// Checkpoints: a Governed→Accelerate switch does NOT surface checkpoint
// UI here. The backend re-seeds the default Phase 1/5/7 EXIT checkpoints
// on that transition (projects.ts:1304-1306). Checkpoint-choice-at-switch
// is a deliberate follow-on if customers want it.

import { useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import DeliveryModeBadge from './DeliveryModeBadge';

type DeliveryMode = 'governed' | 'accelerate';

const MODE_OPTIONS: Array<{ value: DeliveryMode; label: string; helper: string; accent: string; selectedBg: string }> = [
  {
    value:      'governed',
    label:      'Governed',
    helper:     'Every phase exit waits for human approval.',
    accent:     'var(--primary)',
    selectedBg: 'rgba(124,58,237,0.06)',
  },
  {
    value:      'accelerate',
    label:      'Accelerate',
    helper:     'The Phase Gate Agent transitions phases autonomously when their gates pass.',
    accent:     'var(--signal)',
    selectedBg: 'rgba(0,229,160,0.06)',
  },
];

interface DeliveryModeSettingsCardProps {
  projectId: string;
  /** Current delivery mode stored on `projects.delivery_mode`. */
  currentMode: DeliveryMode;
  /**
   * Tenant billing state from useMyTenant. The switch is TRIAL-only; the
   * gate is FAIL-CLOSED (`=== 'TRIAL'`, no `?? 'TRIAL'` fallback) so an
   * unknown state never enables the editable control. Diverges
   * intentionally from NewProjectModal's fail-OPEN creation default,
   * because the safety polarity is inverted here.
   */
  billingState?: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
  /** True while useMyTenant is still resolving — suppresses the editable control. */
  tenantLoading?: boolean;
  /**
   * Project's current phase. ADVISORY ONLY — used to hint that a switch is
   * likely locked once the project has moved past bootstrap. The backend
   * 409 (delivery_mode_locked) remains the authoritative gate; this hint
   * never disables Apply.
   */
  currentPhase?: string;
  /** Called with the freshly-applied mode so the parent can refresh badge + card. */
  onChange?: (next: DeliveryMode) => void;
}

export default function DeliveryModeSettingsCard({
  projectId, currentMode, billingState, tenantLoading, currentPhase, onChange,
}: DeliveryModeSettingsCardProps) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<DeliveryMode>(currentMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FAIL-CLOSED, INVERTED polarity: editable only for a known TRIAL tenant.
  const isTrialTenant = billingState === 'TRIAL';
  const canEdit = !tenantLoading && isTrialTenant;
  // ADVISORY hint only — backend 409 is authoritative; this does not gate Apply.
  const likelyLocked = currentPhase != null && currentPhase !== 'phase_0_bootstrap';

  const apply = async () => {
    // Same-value PATCH is a backend no-op; skip the round-trip entirely.
    if (pending === currentMode) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await apiFetch<{ deliveryMode: DeliveryMode }>(
        `/projects/${projectId}`,
        { method: 'PATCH', body: JSON.stringify({ deliveryMode: pending }) },
      );
      // Read the mode off the returned row so badge + card stay in sync
      // without a refetch.
      onChange?.(updated.deliveryMode);
      setEditing(false);
    } catch (err) {
      // A4 (#1173) structured error codes, surfaced inline (not a toast).
      if (err instanceof ApiError && err.body.code === 'delivery_mode_locked') {
        // 409 — not knowable up-front (GET carries no lock flag); this is
        // the authoritative lock signal.
        setError('Delivery mode is locked once any phase has begun.');
      } else if (err instanceof ApiError && err.body.code === 'delivery_mode_tier_restricted') {
        // 403 — defensive only; a non-Trial user never gets an enabled
        // control, so this should rarely fire for a real user.
        setError('Delivery-mode switching is only available during the Free Trial.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to change delivery mode');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="delivery-mode-settings-card"
      style={{
        padding: '16px 18px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
          Delivery Mode
        </h3>
        {canEdit && !editing && (
          <button
            data-testid="delivery-mode-change-button"
            onClick={() => { setPending(currentMode); setEditing(true); setError(null); }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--primary)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Change
          </button>
        )}
      </div>

      {!editing && (
        <div data-testid="delivery-mode-current" style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <DeliveryModeBadge mode={currentMode} style={{ alignSelf: 'flex-start' }} />
          {/* Non-Trial tenants: read-only, with the reason the control is
              fixed. Suppressed while tenant state is still loading so we
              don't flash the wrong note (fail-closed). */}
          {!tenantLoading && !isTrialTenant && (
            <p data-testid="delivery-mode-fixed-note" style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
              Delivery mode is fixed on paid plans. It can only be changed during the Free Trial.
            </p>
          )}
        </div>
      )}

      {editing && (
        <div data-testid="delivery-mode-editor" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div role="radiogroup" aria-label="Delivery Mode" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MODE_OPTIONS.map(opt => {
              const selected = pending === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`delivery-mode-option-${opt.value}`}
                  onClick={() => setPending(opt.value)}
                  disabled={busy}
                  style={{
                    textAlign:    'left',
                    padding:      12,
                    borderRadius: 'var(--radius-sm)',
                    border:       selected ? `2px solid ${opt.accent}` : '1px solid var(--border)',
                    background:   selected ? opt.selectedBg : 'var(--surface)',
                    cursor:       busy ? 'default' : 'pointer',
                    outline:      'none',
                  }}
                >
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                    {opt.label}
                  </div>
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {opt.helper}
                  </div>
                </button>
              );
            })}
          </div>

          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            The change applies to the next phase transition; any in-progress transition completes under the current mode.
          </p>

          {/* ADVISORY ONLY — never disables Apply; the backend 409 is the
              source of truth. */}
          {likelyLocked && (
            <p data-testid="delivery-mode-lock-hint" style={{ fontSize: 11, color: 'var(--warning)', margin: 0 }}>
              This project has moved past its initial phase — switching may be locked.
            </p>
          )}

          {error && (
            <p data-testid="delivery-mode-error" style={{ fontSize: 12, color: 'var(--error)', margin: 0 }}>{error}</p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              data-testid="delivery-mode-apply"
              onClick={apply}
              disabled={busy || pending === currentMode}
              style={{
                padding: '6px 14px',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                fontWeight: 600,
                cursor: busy || pending === currentMode ? 'default' : 'pointer',
                opacity: busy || pending === currentMode ? 0.6 : 1,
              }}
            >
              {busy ? 'Applying…' : 'Apply'}
            </button>
            <button
              data-testid="delivery-mode-cancel"
              onClick={() => { setEditing(false); setError(null); }}
              disabled={busy}
              style={{
                padding: '6px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
